import axios from 'axios';

import { extractAttributionFromQuery } from '@/shared/lib/attribution';
import { hasAnalyticsConsent as hasAnalyticsConsentStored } from '@/shared/lib/consent';

export type AnalyticsEventType =
  | 'page_view'
  | 'lead_submit'
  | 'cta_click'
  | 'heartbeat'
  | 'scroll_depth';

export interface AnalyticsPayload {
  [key: string]: string | number | boolean | null | undefined;
}

export interface AnalyticsContext {
  visitorId: string;
  sessionId: string;
}

type TouchParams = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  referrer?: string;
  landing_path?: string;
};

type VisitorSession = {
  visitorId: string;
  sessionId: string;
  sessionExpiresAt: number;
};

type SessionState = {
  sessionId: string;
  expiresAt: number;
  utmSignature: string;
};

const VISITOR_COOKIE = 'plf_visitor_id';
const VISITOR_STORAGE_KEY = 'plf_visitor_id';
const SESSION_COOKIE = 'plf_session_id';
const SESSION_STORAGE_KEY = 'plf_session_state';
const SESSION_TTL_MS = 60 * 60 * 1000; // 60 minutes

const randomId = () => crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const now = () => Date.now();

const setCookie = (name: string, value: string, days: number) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  const isSecure = window.location.protocol === 'https:';
  const sameSite = isSecure ? 'None' : 'Lax';
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/;SameSite=${sameSite}${
    isSecure ? ';Secure' : ''
  }`;
};

const getCookie = (name: string) => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

const parseQuery = (search: string): Record<string, string> => {
  const params = new URLSearchParams(search);
  const result: Record<string, string> = {};
  params.forEach((value, key) => {
    result[key] = value;
  });
  return result;
};

const extractTouch = (url: URL): TouchParams => {
  const query = parseQuery(url.search);
  return {
    utm_source: query.utm_source,
    utm_medium: query.utm_medium,
    utm_campaign: query.utm_campaign,
    utm_content: query.utm_content,
    utm_term: query.utm_term,
    referrer: document.referrer || undefined,
    landing_path: url.pathname + url.search,
  };
};

const utmSignatureForUrl = (url: URL): string => {
  const query = parseQuery(url.search);
  return [query.utm_source || '', query.utm_medium || '', query.utm_campaign || ''].join('|');
};

export const hasAnalyticsConsent = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  return hasAnalyticsConsentStored();
};

const readSessionState = (): SessionState | null => {
  const raw = localStorage.getItem(SESSION_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as SessionState;
    if (!parsed?.sessionId || !parsed?.expiresAt) return null;
    return parsed;
  } catch (_err) {
    return null;
  }
};

const saveSessionState = (state: SessionState) => {
  localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(state));
};

const ensureVisitorSession = (): VisitorSession | null => {
  if (!hasAnalyticsConsent()) {
    return null;
  }

  const currentUrl = new URL(window.location.href);
  const currentUtmSignature = utmSignatureForUrl(currentUrl);
  const tsNow = now();

  const storedVisitor = getCookie(VISITOR_COOKIE) || localStorage.getItem(VISITOR_STORAGE_KEY);
  const visitorId = storedVisitor || randomId();
  if (!storedVisitor) {
    setCookie(VISITOR_COOKIE, visitorId, 365);
    localStorage.setItem(VISITOR_STORAGE_KEY, visitorId);
  }

  const sessionState = readSessionState();
  const cookieSession = getCookie(SESSION_COOKIE);
  const hasTimedOut = !sessionState || sessionState.expiresAt <= tsNow;
  const utmChanged = Boolean(sessionState && sessionState.utmSignature !== currentUtmSignature);
  const shouldRotate = hasTimedOut || utmChanged;

  const sessionId = shouldRotate
    ? randomId()
    : (sessionState?.sessionId || cookieSession || randomId());

  const expiresAt = tsNow + SESSION_TTL_MS;
  const nextState: SessionState = {
    sessionId,
    expiresAt,
    utmSignature: currentUtmSignature,
  };
  saveSessionState(nextState);
  setCookie(SESSION_COOKIE, sessionId, 1);

  return { visitorId, sessionId, sessionExpiresAt: expiresAt };
};

const sendEvent = async (type: AnalyticsEventType, payload: AnalyticsPayload, touch: TouchParams) => {
  const session = ensureVisitorSession();
  if (!session) {
    return;
  }

  const currentUrl = new URL(window.location.href);
  const markers = extractAttributionFromQuery(currentUrl.search);
  const body = {
    type,
    occurred_at: new Date().toISOString(),
    visitor_id: session.visitorId,
    session_id: session.sessionId,
    session_expires_at: new Date(session.sessionExpiresAt).toISOString(),
    touch,
    payload,
    lid: markers.lid || undefined,
    source: markers.source || undefined,
  };

  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(body)], { type: 'application/json' });
      navigator.sendBeacon('/api/analytics/events', blob);
      return;
    }
    await axios.post('/analytics/events', body, { withCredentials: true });
  } catch (err) {
    // Analytics is best-effort and must not block product actions.
    console.warn('Analytics event failed', err);
  }
};

const trackPageView = (url: string) => {
  if (!hasAnalyticsConsent()) {
    return;
  }
  try {
    const parsed = new URL(url, window.location.origin);
    const touch = extractTouch(parsed);
    void sendEvent('page_view', { path: parsed.pathname }, touch);
  } catch (err) {
    console.warn('Analytics page_view failed', err);
  }
};

export const getAnalyticsContext = (): AnalyticsContext | null => {
  const session = ensureVisitorSession();
  if (!session) {
    return null;
  }
  return {
    visitorId: session.visitorId,
    sessionId: session.sessionId,
  };
};

export const analytics = {
  hasConsent: hasAnalyticsConsent,
  getContext: getAnalyticsContext,
  trackPageView,
  trackLeadSubmit: (payload: AnalyticsPayload = {}) => {
    if (!hasAnalyticsConsent()) {
      return;
    }
    const touch = extractTouch(new URL(window.location.href));
    void sendEvent('lead_submit', payload, touch);
  },
  trackCtaClick: (payload: AnalyticsPayload = {}) => {
    if (!hasAnalyticsConsent()) {
      return;
    }
    const touch = extractTouch(new URL(window.location.href));
    void sendEvent('cta_click', payload, touch);
  },
};
